import * as i0 from '@angular/core';
import { NgModule, InjectionToken, Injectable, Optional, Inject, Pipe } from '@angular/core';
import * as i2 from '@angular/router';
import { from, switchMap, NEVER, of, BehaviorSubject } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import * as i1$1 from '@angular/cdk/layout';
import { formatDate as formatDate$1, formatCurrency } from '@angular/common';
import * as i1$2 from '@angular/platform-browser';

class AsolCoreModule {
}
AsolCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AsolCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolCoreModule });
AsolCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolCoreModule, imports: [[]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                }]
        }] });

class AuthenticationServiceBase {
    constructor() {
        /** User data from profile */
        this.userData = null;
    }
}

const AUTH_REDIRECT = new InjectionToken('AuthRedirect');

class AuthGuard {
    constructor(authService, router, authRedirect) {
        this.authService = authService;
        this.router = router;
        this.authRedirect = authRedirect;
    }
    canActivate(route, state) {
        return this.authService.isLoggedIn().then((isValid) => {
            var _a;
            if (isValid) {
                return true;
            }
            // navigate to landing page
            this.router.navigate([(_a = this.authRedirect) !== null && _a !== void 0 ? _a : '/']);
            return false;
            // TODO treba doriesit state v pripade deep linku
            // this.authService.login();
        });
    }
}
AuthGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthGuard, deps: [{ token: AuthenticationServiceBase }, { token: i2.Router }, { token: AUTH_REDIRECT, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AuthGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: AuthenticationServiceBase }, { type: i2.Router }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [AUTH_REDIRECT]
                    }] }];
    } });

const HOME_REDIRECT = new InjectionToken('HomeRedirect');

const LOGIN_REDIRECT = new InjectionToken('LoginRedirect');

class HomeGuard {
    constructor(authService, router, homeRedirect, loginRedirect) {
        this.authService = authService;
        this.router = router;
        this.homeRedirect = homeRedirect;
        this.loginRedirect = loginRedirect;
    }
    canActivate(route, state) {
        return this.authService.isLoggedIn().then((isValid) => {
            var _a;
            if (!isValid) {
                /** in case there are multiple ways how to log into application */
                if (this.loginRedirect) {
                    this.router.navigate([this.loginRedirect]);
                }
                return true;
            }
            /** in case home is on another page */
            const home = (_a = this.homeRedirect) !== null && _a !== void 0 ? _a : '/dashboard';
            // navigate to landing page
            this.router.navigate([home]);
            return true;
        });
    }
}
HomeGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HomeGuard, deps: [{ token: AuthenticationServiceBase }, { token: i2.Router }, { token: HOME_REDIRECT, optional: true }, { token: LOGIN_REDIRECT, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
HomeGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HomeGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: HomeGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: AuthenticationServiceBase }, { type: i2.Router }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [HOME_REDIRECT]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [LOGIN_REDIRECT]
                    }] }];
    } });

/**
 * Check URL if it is in anonym URL list or not
 * @param url URL to check
 * @param anonymUrls anonym URL
 * @returns True if URL is anonym
 */
function isAnonym(url, anonymUrls) {
    if (!(anonymUrls === null || anonymUrls === void 0 ? void 0 : anonymUrls.length)) {
        return false;
    }
    const urlObj = new URL(url.toLocaleLowerCase());
    const path = urlObj.pathname;
    return anonymUrls.some((v) => path.includes(v.toLocaleLowerCase()));
}

const ANONYM_URL = new InjectionToken('AnonymUrls');

class AuthInterceptorService {
    constructor(authService, anonymUrls) {
        this.authService = authService;
        this.anonymUrls = anonymUrls;
    }
    intercept(req, next) {
        return from(this.authService.getAccessToken()).pipe(switchMap((token) => {
            const url = req.url.toLowerCase();
            if ((url.includes('/api/') || url.includes('/odata/')) &&
                !isAnonym(url, this.anonymUrls)) {
                // Here we should add token, if it is not provided cancel the request
                if (!token) {
                    return NEVER;
                }
                const header = 'Bearer ' + token;
                const headers = req.headers
                    .set('Authorization', header)
                    .set('Cache-Control', 'no-cache');
                req = req.clone({ headers });
            }
            return next.handle(req);
        }));
    }
}
AuthInterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthInterceptorService, deps: [{ token: AuthenticationServiceBase }, { token: ANONYM_URL, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
AuthInterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthInterceptorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AuthInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: AuthenticationServiceBase }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [ANONYM_URL]
                    }] }];
    } });

const CORE_CONSTANTS = {
    DEFAULT_RETURN_VALUE: '...',
    DEFAULT_DATE_FORMAT: 'mediumDate',
    DEFAULT_TIME_FORMAT: 'short',
    TENANT_STORAGE: 'selectedTenant',
};

class TenantInterceptorService {
    constructor(anonymUrls) {
        this.anonymUrls = anonymUrls;
    }
    intercept(req, next) {
        const url = req.url.toLowerCase();
        if ((!url.includes('/api/') && !url.includes('/odata/')) ||
            url.includes('/assets/') ||
            isAnonym(url, this.anonymUrls)) {
            return next.handle(req);
        }
        const tenantId = sessionStorage.getItem(CORE_CONSTANTS.TENANT_STORAGE);
        if (!tenantId) {
            return NEVER;
        }
        const headers = req.headers.set('X-Tenant-Id', tenantId);
        req = req.clone({ headers });
        return next.handle(req);
    }
}
TenantInterceptorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TenantInterceptorService, deps: [{ token: ANONYM_URL, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
TenantInterceptorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TenantInterceptorService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TenantInterceptorService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [ANONYM_URL]
                    }] }];
    } });

const CONTENT_TYPE = {
    JSON: 'application/json',
    PLAIN: 'text/plain',
    CSS: 'text/css',
    HTML: 'text/html',
    JAVASCRIPT: 'text/javascript',
    OCTET_STREAM: 'application/octet-stream',
    IMAGE_ALL: 'image/*',
    IMAGE_GIF: 'image/gif',
    IMAGE_JPEG: 'image/jpeg',
    IMAGE_PNG: 'image/png',
    IMAGE_SVG: 'image/svg+xml',
    AUDIO_WEBM: 'audio/webm',
    AUDIO_OGG: 'audio/ogg',
    VIDEO_WEBM: 'video/webm',
    VIDEO_OGG: 'video/ogg',
    OGG: 'application/ogg',
    FORM_DATA: 'multipart/form-data',
    BYTE_RANGES: 'multipart/byteranges',
    WORD: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    EXCEL: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

class BaseDataService {
    createHttpParams(searchParams) {
        let parameters = new HttpParams();
        if (searchParams) {
            for (const [paramName, value] of Object.entries(searchParams)) {
                if (value === undefined || value === null) {
                    continue;
                }
                if (value instanceof Array) {
                    if (!value.length) {
                        continue;
                    }
                    parameters = parameters.append(paramName, JSON.stringify(value));
                }
                else {
                    if (typeof value === 'string') {
                        parameters = parameters.append(paramName, value);
                    }
                    else if (typeof value === 'number') {
                        parameters = parameters.append(paramName, value.toString());
                    }
                    else if (typeof value === 'boolean') {
                        parameters = parameters.append(paramName, value.toString());
                    }
                    else if (value instanceof Date) {
                        parameters = parameters.append(paramName, value.toISOString());
                    }
                    else if (typeof value === 'object' && value) {
                        parameters = parameters.append(paramName, JSON.stringify(value));
                    }
                    else {
                        throw new Error('Unknown http parameter type');
                    }
                }
            }
        }
        return parameters;
    }
    getHeaders(contentType) {
        const type = contentType ? contentType : CONTENT_TYPE.JSON;
        return (new HttpHeaders()
            // .set('Accept', type)
            .set('Content-Type', type));
    }
    getFileDownloadHeaders() {
        return new HttpHeaders().set('Content-Type', CONTENT_TYPE.OCTET_STREAM);
    }
}

const API_BASE_URI = new InjectionToken('ApiBaseUri');

class ApiDataService extends BaseDataService {
    constructor(httpClient, baseUri = '') {
        super();
        this.httpClient = httpClient;
        this.baseUri = baseUri;
    }
    /**
     * Sends GET request with response to URL and search params, mostly used to get attachments, streams, etc.
     * @param url URL to sent request to
     * @param searchParams Optional search params
     * @returns Observable of T
     */
    getResponse(url, searchParams) {
        const parameters = this.createHttpParams(searchParams);
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            observe: 'response',
            params: parameters,
        });
    }
    /**
     * Sends GET request to URL and data
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Collection of observable T
     */
    getAll(url, data) {
        const parameters = this.createHttpParams(data);
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            params: parameters,
        });
    }
    /**
     * Sends GET request to collection of data
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Collection of observable T @see{@link DataResult}
     */
    getCollection(url, data) {
        const parameters = this.createHttpParams(data);
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            params: parameters,
        });
    }
    /**
     * Sends POST request to URL to GET data for grid with filter
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Observable of T
     */
    getGridDataWithFilter(url, data) {
        return this.httpClient.post(`${this.baseUri}${url}`, data, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
    /**
     * Sends GET request to URL and search params
     * @param url URL to sent request to
     * @param searchParams Optional search params
     * @returns One Observable of T
     */
    getOne(url, searchParams) {
        const parameters = this.createHttpParams(searchParams);
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            params: parameters,
            observe: 'body',
        });
    }
    /**
     * Sends POST request to URL with payload
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Observable of T
     */
    postData(url, data) {
        return this.httpClient.post(`${this.baseUri}${url}`, data, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
    /**
     * Sends PUT request to URL and data
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Observable T
     */
    put(url, data) {
        return this.httpClient.put(`${this.baseUri}${url}`, data, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
    /**
     * Sends PATCH request to URL and data
     * @param url URL to sent request to
     * @param data Data to send
     * @returns Observable T
     */
    patch(url, data) {
        return this.httpClient.patch(`${this.baseUri}${url}`, data, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
    /**
     * Sends DELETE request with Id
     * @param url URL to sent request to
     * @param id Entity identifier
     * @returns Observable T
     */
    delete(url, id) {
        return this.httpClient.delete(`${this.baseUri}${url}` + '/' + id, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
    /**
     * Sends DELETE request with data in QS
     * @param url URL to sent request to
     * @param data Data to be send
     * @returns Observable of T
     */
    deleteData(url, data) {
        const parameters = this.createHttpParams(data);
        return this.httpClient.delete(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            observe: 'body',
            params: parameters,
        });
    }
    /**
     * Sends DELETE request with data in body
     * @param url URL to sent request to
     * @param data Data to be send
     * @returns Observable of T
     */
    deleteBodyData(url, data) {
        return this.httpClient.delete(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            observe: 'body',
            body: data,
        });
    }
    /**
     * Sends POST request to url without payload
     * @param url URL to sent request to
     * @returns Observable T
     */
    post(url) {
        return this.httpClient.post(`${this.baseUri}${url}`, {
            headers: this.getHeaders(),
            observe: 'body',
        });
    }
}
ApiDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ApiDataService, deps: [{ token: i1.HttpClient }, { token: API_BASE_URI, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
ApiDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ApiDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ApiDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [API_BASE_URI]
                    }] }];
    } });

class FileDataService extends BaseDataService {
    constructor(httpClient, baseUri = '') {
        super();
        this.httpClient = httpClient;
        this.baseUri = baseUri;
    }
    /**
     * Gets file based on URI
     * @param uri URL to get file from
     * @returns File as blob
     */
    get(url) {
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getFileDownloadHeaders(),
            responseType: 'blob',
        });
    }
    /**
     * Gets file based on URI
     * @param uri URL to get file from
     * @returns File as blob
     */
    getFile(url) {
        return this.httpClient.get(`${this.baseUri}${url}`, {
            headers: this.getFileDownloadHeaders(),
            responseType: 'blob',
            observe: 'response',
        });
    }
    /**
     * Uploads file
     * @param uri URI to upload file to
     * @param file File to upload
     * @returns true/false as upload execution result
     */
    upload(url, file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.httpClient.post(`${this.baseUri}${url}`, formData, {
            observe: 'body',
            headers: {
                'Content-Type': 'undefined',
            },
        });
    }
    /**
     * Uploads file
     * @param uri URI to upload file to
     * @param file File to upload
     * @returns true/false as upload execution result
     */
    uploadFile(uri, file) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.httpClient.post(uri, formData, {
            observe: 'response',
            headers: {
                'Content-Type': 'undefined',
            },
        });
    }
    /**
     * Uploads file with additional data
     * @param uri URI to upload file to
     * @param file File to upload
     * @param data additional data to send
     * @param contentType content type of data
     * @returns true/false as upload execution result
     */
    uploadData(url, file, data, contentType) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('data', JSON.stringify(data));
        const headers = contentType ? { 'Content-Type': contentType } : undefined;
        return this.httpClient.post(`${this.baseUri}${url}`, formData, {
            observe: 'body',
            headers: headers,
        });
    }
    /**
     * Uploads file with additional data
     * @param uri URI to upload file to
     * @param file File to upload
     * @param data additional data to send
     * @returns true/false as upload execution result
     */
    uploadFileData(uri, file, data) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('data', JSON.stringify(data));
        return this.httpClient.post(uri, formData, {
            observe: 'response',
            headers: {
                'Content-Type': 'undefined',
            },
        });
    }
    /**
     * Downloads file from browser by URL
     * @param url URL to get file from
     */
    downloadFile(url) {
        return this.getFile(url).pipe(switchMap((response) => {
            if (!response.body) {
                return of(false);
            }
            this.downloadDocument(response);
            return of(true);
        }));
    }
    /**
     * Method for showing document in new tab
     *
     * @public
     * @param {HttpResponse<Blob>} response
     */
    showDocument(response) {
        var _a;
        if (!response.body || !response.headers.get('Content-Type')) {
            return;
        }
        window.open(window.URL.createObjectURL(new Blob([response.body], {
            type: (_a = response.headers.get('Content-Type')) !== null && _a !== void 0 ? _a : '',
        })), '_blank');
    }
    /**
     * Downloads file/document from response
     *
     * @public
     * @param {HttpResponse<Blob>} response
     */
    downloadDocument(response) {
        if (!response.body) {
            return;
        }
        const contentDisposition = response.headers.get('content-disposition');
        let filename = '';
        if (!contentDisposition) {
            filename = 'file';
        }
        else {
            filename = contentDisposition
                .split(';')[1]
                .split('filename')[1]
                .split('=')[1]
                .trim();
        }
        /** build and download file from browser */
        const dataType = response.body.type;
        const binaryData = [response.body];
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }
}
FileDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FileDataService, deps: [{ token: i1.HttpClient }, { token: API_BASE_URI, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FileDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FileDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FileDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [API_BASE_URI]
                    }] }];
    } });

const MEDIA_QUERY = {
    MENU_BREAK: '(max-width: 993px)',
    XS: '(max-width: 575.98px)',
    SM: '(min-width: 576px) and (max-width: 767.98px)',
    MD: '(min-width: 768px) and (max-width: 991.98px)',
    LG: '(min-width: 992px) and (max-width: 1199.98px)',
    XL: '(min-width: 1200px) and (max-width: 1399.98px)',
    XXL: '(min-width: 1400px)',
};

class AsolMediaQueryService {
    constructor(media, breakpointObserver) {
        this.mobileQuery = media.matchMedia(MEDIA_QUERY.MENU_BREAK);
        this.mobileViewportChanged$ = breakpointObserver.observe(MEDIA_QUERY.MENU_BREAK);
        this.viewportChanged$ = breakpointObserver.observe([
            MEDIA_QUERY.XS,
            MEDIA_QUERY.SM,
            MEDIA_QUERY.MD,
            MEDIA_QUERY.LG,
            MEDIA_QUERY.XL,
            MEDIA_QUERY.XXL,
        ]);
    }
    /**
     * Checks if media query is mobile or desktop
     * @returns True if mobile version, false if desktop
     */
    isMobile() {
        return this.mobileQuery.matches;
    }
}
AsolMediaQueryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolMediaQueryService, deps: [{ token: i1$1.MediaMatcher }, { token: i1$1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable });
AsolMediaQueryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolMediaQueryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AsolMediaQueryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.MediaMatcher }, { type: i1$1.BreakpointObserver }]; } });

const GENERAL_TRANSLATION = 'asol-core-translation-general';
const ERROR_TRANSLATION = 'asol-core-translation-error';

const DEFAULT_LANGUAGE = new InjectionToken('DefaultLanguage');

const LANGUAGES = new InjectionToken('TranslationLanguages');

var errorTranslation = {
    "en-US": {
        genericErrorHeader: "Error occurred",
        validationErrorHeader: "Validation error",
        unexpectedErrorText: "Unexpected error occurred. Please contact administrators",
        unknownErrorText: "Unknown error occurred. Please contact administrators",
        internalErrorText: "Internal error occurred. Please contact administrators",
        passwordConfirmMatchError: "Passwords must match",
        requiredErrorMessage: "Required value",
        generalValidationErrorMessage: "Value is not valid",
        dateRangeErrorMessage: "Start date must be before end date",
        minValueLength: "Minimum text length is {0}, but provided was {1}",
        maxValueLength: "Maximum text length is {0}, but provided was {1}",
        minValue: "Minimum value is {0}, but provided was {1}",
        maxValue: "Maximum value is {0}, but provided was {1}",
        duplicate: "Item already exists",
        unauthorizedErrorHeader: "Unauthorized",
        identityErrorHeader: "Maintenance in progress",
        maxValuesErrorMessage: "You have selected too many answers",
        minValuesErrorMessage: "You have selected too few answers"
    },
    "cs-CZ": {
        genericErrorHeader: "Nastala chyba",
        validationErrorHeader: "Chyba ověření",
        unexpectedErrorText: "Došlo k neočekávané chybě. Kontaktujte správce.",
        unknownErrorText: "Došlo k neznámé chybě. Kontaktujte správce.",
        internalErrorText: "Došlo k vnitřní chybě. Kontaktujte správce.",
        passwordConfirmMatchError: "Hesla se musí shodovat",
        requiredErrorMessage: "Povinná hodnota",
        generalValidationErrorMessage: "Hodnota není validní",
        dateRangeErrorMessage: "Počáteční datum musí být před koncovým",
        minValueLength: "Minimální délka textu je {0}, ale zadali jste {1}",
        maxValueLength: "Maximální délka textu je {0}, ale zadali jste {1}",
        minValue: "Minimální hodnota je {0}, ale zadali jste {1}",
        maxValue: "Maximální hodnota je {0}, ale zadali jste {1}",
        duplicate: "Položka již existuje",
        unauthorizedErrorHeader: "Nedostatečné oprávnění",
        identityErrorHeader: "Momentálně probíhá údržba",
        maxValuesErrorMessage: "Vybrali jste příliš mnoho odpovědí",
        minValuesErrorMessage: "Vybrali jste příliš málo odpovědí"
    },
    "sk-SK": {
        genericErrorHeader: "Nastala chyba",
        validationErrorHeader: "Chyba overenia",
        unexpectedErrorText: "Vyskytla sa neočakávaná chyba. Obráťte sa na správcov.",
        unknownErrorText: "Vyskytla sa neznáma chyba. Obráťte sa na správcov.",
        internalErrorText: "Vyskytla sa interná chyba. Obráťte sa na správcov.",
        passwordConfirmMatchError: "Heslá sa musia zhodovať",
        requiredErrorMessage: "Povinná hodnota",
        generalValidationErrorMessage: "Hodnota nie je validná",
        dateRangeErrorMessage: "Počiatočný dátum musí byť pred koncovým",
        minValueLength: "Minimálna dľžka textu je {0}, ale zadali ste {1}",
        maxValueLength: "Maximálna dľžka textu je {0}, ale zadali ste {1}",
        minValue: "Minimálna hodnota je {0}, ale zadali ste {1}",
        maxValue: "Maximálna hodnota je {0}, ale zadali ste {1}",
        duplicate: "Položka už existuje",
        unauthorizedErrorHeader: "Nedostatočné oprávnenie",
        identityErrorHeader: "Momentálne prebieha údržba",
        maxValuesErrorMessage: "Vybrali ste príliš veľa odpovedí",
        minValuesErrorMessage: "Vybrali ste príliš málo odpovedí"
    }
};

var translation = {
    "sk-SK": {
        change: "Zmeniť",
        save: "Uložiť",
        close: "Zatvoriť",
        cancel: "Zrušiť",
        back: "Späť",
        next: "Ďalej",
        download: "Stiahnuť",
        print: "Vytlačiť",
        view: "Zobraziť",
        reset: "Vymazať",
        submit: "Odoslať",
        saved: "Uložené!",
        error: "Vznikla chyba",
        ok: "Ok",
        "delete": "Zmazať",
        duplicate: "Duplikovať",
        split: "Rozdeliť",
        edit: "Editovať",
        done: "Hotovo",
        yes: "Áno",
        no: "Nie",
        add: "Pridať",
        create: "Vytvoriť",
        remove: "Odstrániť",
        noData: "Žiadne dáta na zobrazenie",
        search: "Vyhľadať",
        enable: "Povoliť",
        disable: "Zakázať",
        enabled: "Povolené",
        disabled: "Zakázané",
        more: "viac",
        validFrom: "Platnosť od",
        validTo: "Platnosť do",
        discard: "Zahodiť zmeny",
        respond: "Odpovedať",
        send: "Odoslať",
        confirm: "Potvrdiť",
        "import": "Importovať",
        items: "Položky",
        days1: "deň",
        days2: "dni",
        days5: "dní",
        minutes1: "minuta",
        minutes2: "minuty",
        minutes5: "minút",
        hours1: "hodina",
        hours2: "hodiny",
        hours5: "hodín",
        startswith: "Začína s",
        endswith: "Končí s",
        contains: "Obsahuje",
        equal: "Rovná sa",
        notequal: "Nerovná sa",
        lessthan: "Menej ako",
        lessthanorequal: "Menej ako alebo rovné",
        greaterthan: "Väčší než",
        greaterthanorequal: "Väčšie alebo rovné",
        between: "Medzi",
        notbetween: "Nie medzi",
        "in": "Nachádza sa v",
        notin: "Nenachádza sa v",
        OtherFields: "Iné polia",
        isempty: "Je prázdny",
        isnotempty: "Nie je prázdne",
        isnull: "Je Null",
        isnotnull: "Nie je Null"
    },
    "cs-CZ": {
        change: "Změnit",
        save: "Uložit",
        close: "Zavřít",
        cancel: "Zrušit",
        back: "Zpět",
        next: "Dále",
        download: "Stáhnout",
        print: "Vytisknout",
        view: "Zobrazit",
        reset: "Vymazat",
        submit: "Odeslat",
        saved: "Uloženo!",
        error: "Vznikla chyba",
        ok: "Ok",
        "delete": "Smazat",
        duplicate: "Duplikovat",
        split: "Rozdělit",
        edit: "Editovat",
        done: "Hotovo",
        yes: "Ano",
        no: "Ne",
        add: "Přidat",
        create: "Vytvořit",
        remove: "Odstranit",
        noDataData: "Žádné data na zobrazení",
        search: "Vyhledat",
        enable: "Povolit",
        disable: "Zakázat",
        enabled: "Povoleno",
        disabled: "Zakázáno",
        more: "více",
        validFromFrom: "Platnost od",
        validToTo: "Platnost do",
        discard: "Zahodit změny",
        respond: "Odpovědět",
        send: "Odeslat",
        confirm: "Potvrdit",
        "import": "Importovat",
        days1: "den",
        days2: "dni",
        days5: "dn",
        minutes1: "minuta",
        minutes2: "minuty",
        minutes5: "minut",
        hours1: "hodina",
        hours2: "hodiny",
        hours5: "hodín",
        startswith: "Začíná s",
        endswith: "Končí s",
        contains: "Obsahuje",
        equal: "Rovná se",
        notequal: "Nerovná se",
        lessthan: "Méně než",
        lessthanorequal: "Menší nebo rovno",
        greaterthan: "Větší než",
        greaterthanorequal: "Větší nebo rovno",
        between: "Mezi",
        notbetween: "Není mezi",
        "in": "Nachází se v",
        notin: "Nenachází se v",
        isempty: "Je prázdný",
        isnotempty: "Není prázdný",
        isnull: "Je Null",
        isnotnull: "Není Null"
    },
    "en-US": {
        change: "Change",
        save: "Save",
        close: "Close",
        cancel: "Cancel",
        back: "Back",
        next: "Next",
        download: "Download",
        print: "Print",
        view: "View",
        reset: "Delete",
        submit: "Submit",
        saved: "Saved!",
        error: "Error occured",
        ok: "Ok",
        "delete": "Delete",
        duplicate: "Duplicate",
        split: "Split",
        edit: "Edit",
        done: "Done",
        yes: "Yes",
        no: "No",
        add: "Add",
        create: "Create",
        remove: "Remove",
        noDataData: "No data to display",
        search: "Search",
        enable: "Enable",
        disable: "Disable",
        enabled: "Enabled",
        disabled: "Disabled",
        more: "more",
        validFromFrom: "Valid from",
        validToTo: "Valid to",
        discard: "Discard changes",
        respond: "Respond",
        send: "Send",
        confirm: "Confirm",
        "import": "Import",
        days1: "day",
        days2: "days",
        days5: "days",
        minutes1: "minut",
        minutes2: "minuts",
        minutes5: "minuts",
        hours1: "hour",
        hours2: "hours",
        hours5: "hours",
        startswith: "Starts With",
        endswith: "Ends With",
        contains: "Contains",
        equal: "Equal",
        notequal: "Not Equal",
        lessthan: "Less Than",
        lessthanorequal: "Less Than Or Equal",
        greaterthan: "Greater Than",
        greaterthanorequal: "Greater Than Or Equal",
        between: "Between",
        notbetween: "Not Between",
        "in": "In",
        notin: "Not In",
        isempty: "Is Empty",
        isnotempty: "Is Not Empty",
        isnull: "Is Null",
        isnotnull: "Is Not Null"
    }
};

class TranslationService {
    constructor(languages, defaultLanguage) {
        this.languages = languages;
        this.defaultLanguage = defaultLanguage;
        this.transDictionary = [];
        this.languageChangedObservable = new BehaviorSubject({
            currentLocale: this.currentLocale,
            isInteracted: false,
        });
        this.languageChanged$ = this.languageChangedObservable.asObservable();
        this.locale = this.defaultLanguage.short;
        this.initialize(GENERAL_TRANSLATION, translation);
        this.initialize(ERROR_TRANSLATION, errorTranslation);
    }
    /** Sets the locale
     * @param value new local value
     */
    set currentLocale(value) {
        this.locale = value;
    }
    get currentLocale() {
        var _a;
        return (_a = this.locale) !== null && _a !== void 0 ? _a : this.defaultLanguage.short;
    }
    /**
     * Initialize translation
     * @param key Translation key for the registration
     * @param data Translation data
     * @returns void
     */
    initialize(key, data) {
        if (this.transDictionary.some((p) => p.key === key)) {
            return;
        }
        const item = {
            key,
            data,
        };
        this.transDictionary.push(item);
    }
    /**
     * Removes translation from collection
     * @param key Translation key to remove
     */
    removeTranslation(key) {
        const index = this.transDictionary.findIndex((p) => p.key === key);
        if (index === -1) {
            return;
        }
        this.transDictionary.splice(index, 1);
    }
    /**
     * Gets translation for given key and value
     * @param key Translation item e.g. module key
     * @param value Translation value e.g. file
     * @returns string translation or missing text
     */
    get(key, value) {
        const missing = '@@Missing:' + key + '.' + value;
        if (!this.transDictionary.some((p) => p.key === key)) {
            return missing;
        }
        const data = this.transDictionary.find((p) => p.key === key);
        return this.translate(value, data === null || data === void 0 ? void 0 : data.data);
    }
    /**
     * Gets translation for given translation file and value
     * @param value Translation value
     * @param translationFile Translation json file
     * @returns string translation or missing text
     */
    translate(value, translationFile) {
        const missing = '@@Missing:' + value;
        if (!translationFile) {
            return missing;
        }
        const translations = translationFile[this.getFullLocale];
        if (!translations) {
            return missing;
        }
        const valueCase = Object.keys(translations).find((key) => key.toLowerCase() === value.toLowerCase());
        if (!valueCase) {
            return missing;
        }
        const translation = translations[valueCase];
        return translation ? translation : missing;
    }
    /** Gets current locale in full format e.g. sk-SK
     * @returns current locale in full format
     */
    get getFullLocale() {
        const language = this.languages.find((p) => p.short === this.currentLocale || p.locale === this.currentLocale);
        if (!language) {
            throw new Error('Missing translation mapping');
        }
        return language.locale;
    }
    /**
     * Sets new language based on provided locale
     * @param locale Locale to change to
     */
    setLocale(locale, isInteracted = false) {
        const language = this.languages.find((p) => p.short === locale || p.locale === locale);
        this.currentLocale = language ? language.short : this.defaultLanguage.short;
        this.languageChangedObservable.next({
            currentLocale: locale,
            isInteracted,
        });
    }
    /**
     * Sets locale by language
     * @param language Language to set
     */
    setLocaleByLanguage(language, isInteracted = false) {
        const lang = this.languages.find((p) => p.name === language);
        if (!lang) {
            return;
        }
        this.currentLocale = lang.short;
        this.languageChangedObservable.next({
            currentLocale: lang.short,
            isInteracted,
        });
    }
    /**
     * Gets current selected language
     * @returns Full Language
     */
    getCurrentLanguage() {
        const language = this.languages.find((p) => p.short === this.currentLocale || p.locale === this.currentLocale);
        if (!language) {
            throw new Error('Missing translation mapping');
        }
        return language;
    }
    /** Gets all supported languages
     * @returns Collection of supported languages @see {@link Language}
     */
    get getSupportedLanguages() {
        return this.languages;
    }
    /**
     * Gets full locale based on currency
     * @param currency Currency to find
     * @returns Language or undefined
     */
    getLocaleForCurrency(currency) {
        if (!currency) {
            throw Error('Missing currency');
        }
        const language = this.languages.find((p) => p.currency === currency);
        if (!language) {
            throw Error(`Missing translation mapping for currency ${currency}`);
        }
        return language;
    }
    /**
     * Returns date format for locale.
     *
     * If no locale provided it returns date format for current locale
     *
     * @returns Date format
     */
    getDateFormatForLocale(locale) {
        const localeToFind = locale !== null && locale !== void 0 ? locale : this.currentLocale;
        const language = this.languages.find((p) => p.short === localeToFind || p.locale === localeToFind);
        return !language ? CORE_CONSTANTS.DEFAULT_DATE_FORMAT : language.dateFormat;
    }
    /**
     * Returns time format for locale.
     *
     * If no locale provided it returns time format for current locale
     *
     * @returns Time format
     */
    getTimeFormatForLocale(locale) {
        const localeToFind = locale !== null && locale !== void 0 ? locale : this.currentLocale;
        const language = this.languages.find((p) => p.short === localeToFind || p.locale === localeToFind);
        return !language ? CORE_CONSTANTS.DEFAULT_DATE_FORMAT : language.timeFormat;
    }
    /**
     * Returns date time format for locale.
     *
     * If no locale provided it returns date time format for current locale
     *
     * @returns Date format
     */
    getDateTimeFormatForLocale(locale) {
        const localeToFind = locale !== null && locale !== void 0 ? locale : this.currentLocale;
        const language = this.languages.find((p) => p.short === localeToFind || p.locale === localeToFind);
        return !language
            ? CORE_CONSTANTS.DEFAULT_DATE_FORMAT
            : `${language.dateFormat} ${language.timeFormat}`;
    }
}
TranslationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TranslationService, deps: [{ token: LANGUAGES }, { token: DEFAULT_LANGUAGE }], target: i0.ɵɵFactoryTarget.Injectable });
TranslationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TranslationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: TranslationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [LANGUAGES]
                    }] }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [DEFAULT_LANGUAGE]
                    }] }];
    } });

class LanguageInterceptor {
    constructor(transService) {
        this.transService = transService;
    }
    intercept(req, next) {
        if (!req.url.includes('api/')) {
            return next.handle(req);
        }
        // Language header
        req = req.clone({
            headers: req.headers.set('x-accepted-language', this.transService.getFullLocale),
        });
        return next.handle(req);
    }
}
LanguageInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LanguageInterceptor, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LanguageInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LanguageInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

const CZ = {
    short: 'cs',
    name: 'Česky',
    locale: 'cs-CZ',
    currency: 'CZK',
    currencySign: 'Kč',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HH:mm',
};

const EN = {
    short: 'en',
    name: 'English',
    locale: 'en-US',
    currency: 'USD',
    currencySign: '$',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: 'HH:mm a',
};

const SK = {
    short: 'sk',
    name: 'Slovensky',
    locale: 'sk-SK',
    currency: 'EUR',
    currencySign: '€',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HH:mm',
};

const MAIN_APP_CONST = {
    SERVICE: 'SHOPSERVICE',
    CRM: 'CRM',
    RENTAL: 'RENTAL',
    MLE: 'MLE',
    CWO: 'CWO',
    ASSECOSTORE: 'ASSECOSTORE',
    MOBILE: 'QASIDAMOBILE',
    ORDERMANAGER: 'ORDERMANAGER',
};

/**
 * Formats date
 * @param value value which needs to be converted
 * @param trans translation service for current locale
 * @param format optional format
 * @param customReturnValue optional return value, default @see CORE_CONSTANTS.DEFAULT_RETURN_VALUE
 * @returns formatted date as string or default value or custom return value
 */
function formatDate(value, trans, format, customReturnValue) {
    var _a;
    if (!value) {
        return customReturnValue !== null && customReturnValue !== void 0 ? customReturnValue : CORE_CONSTANTS.DEFAULT_RETURN_VALUE;
    }
    const returnFormat = format !== null && format !== void 0 ? format : (_a = trans.getCurrentLanguage()) === null || _a === void 0 ? void 0 : _a.dateFormat;
    return formatDate$1(value, returnFormat, trans.currentLocale);
}

/**
 * Checks if property is of type ValueCurrency
 * @param property Property to check
 * @returns True if property is of type ValueCurrency
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isValueCurrency(property) {
    return (typeof property === 'object' &&
        property !== null &&
        'value' in property &&
        'currency' in property);
}

/**
 * Function used to correctly format currency values
 *
 * Internally it uses Translation service to get correct locale based on currency string
 *
 * @param value Value to format of type ValueCurrency @see {@link ValueCurrency}
 * @param trans Translation service
 * @param precision Decimal place precision in string format `{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}`. See `DecimalPipe` for more details.
 * @default 1.2
 */
function valueCurrencyTransform(value, trans, precision = '1.2') {
    var _a, _b, _c, _d;
    if (!value || !value.currency) {
        return '';
    }
    const language = trans.getLocaleForCurrency(value.currency);
    if (!language) {
        return (_b = (_a = value.value) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '0';
    }
    const formattedNumber = formatCurrency((_c = value.value) !== null && _c !== void 0 ? _c : 0, language.locale, language.currency, (_d = value.currency) !== null && _d !== void 0 ? _d : 'EUR', precision);
    return formattedNumber;
}

/**
 * Transforms all complex objects in collection to its simple representation
 * @param collection Collection to transform
 * @param trans Translation service
 * @returns Transformed collection
 */
function collectionMapper(collection, trans) {
    return collection.map((item) => {
        const newItem = Object.assign({}, item);
        Object.keys(newItem).forEach((key) => {
            if (isValueCurrency(newItem[key])) {
                const valueCurrency = newItem[key];
                newItem[key] = valueCurrencyTransform(valueCurrency, trans);
                return newItem;
            }
            // if it is a number return item
            if (!isNaN(+newItem[key])) {
                return newItem;
            }
            if (!isNaN(Date.parse(newItem[key]))) {
                newItem[key] = formatDate(newItem[key], trans);
            }
        });
        return newItem;
    });
}

/**
 * Formats number input to k, m, t, etc.
 * @param amount amount to convert
 * @param locale current locale for translation
 * @param decimals decimals for amount format
 */
function formatAmount(amount, locale, decimals = 2) {
    const SUFFIX_SYMBOLS = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
    if (!amount && amount !== 0) {
        return undefined;
    }
    let scaled = 0;
    let suffix = '';
    if (amount !== 0) {
        // determines SUFFIX symbol
        const tier = Math.floor(Math.log10(Math.abs(amount)) / 3);
        // get suffix and determine scale
        suffix = SUFFIX_SYMBOLS[tier];
        const scale = Math.pow(10, (tier * 3));
        // scale the number
        scaled = amount / scale;
    }
    const value = scaled.toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return { amount: value, suffix: suffix };
}

/**
 * Formats number input into B, KB, MB etc.
 * @param a number to convert
 * @param b decimal places
 * @returns string containing KB, MB ,etc.
 */
function formatBytes(a, b = 2) {
    if (0 === a) {
        return '0 Bytes';
    }
    const c = 0 > b ? 0 : b;
    const d = Math.floor(Math.log(a) / Math.log(1024));
    return (parseFloat((a / Math.pow(1024, d)).toFixed(c)) +
        ' ' +
        ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][d]);
}

/**
 * Generates random identifier
 * @param maxLength Optional max length
 * @returns Generated string
 */
function generateId(maxLength) {
    if (maxLength === 0) {
        throw new Error('Id length must be greater than 0');
    }
    if (maxLength && maxLength <= 0) {
        throw new Error('Id length must be greater than 0');
    }
    return Math.random().toString(36).substr(2, maxLength);
}

/**
 * Gets plural representation translation key
 * @param count Number to get plural
 * @param suffix Suffix text
 * @returns It will combine into this values:
 *  if 1 it returns suffix1
 *  if >1 && <5 returns suffix2
 *  if > 4 returns suffix5
 */
function getPluralTrans(count, suffix) {
    let number = 1;
    if (count > 1 && count < 5) {
        number = 2;
    }
    if (count > 4 || count === 0) {
        number = 5;
    }
    return `${suffix}${number}`;
}

/**
 * Compares two dates and returns difference based on dateDiff settings
 * It compares date2 to date1
 *
 * @param {Date} date1 Date which we will calculate
 * @param {Date} date2 Date which will be used as base
 * @param {DateDiff} [dateDiff='days'] Type of response
 * @returns {string} Returns count based on dateDiff
 */
function getDateDifference(trans, date1, date2, dateDiff = 'days') {
    let textPlural = trans.get(GENERAL_TRANSLATION, `${dateDiff}5`);
    let diff = 5;
    if (!date1 || !date2) {
        return `0 ${textPlural}`;
    }
    try {
        if (typeof date1 === 'string') {
            date1 = new Date(date1);
        }
        if (typeof date2 === 'string') {
            date2 = new Date(date2);
        }
    }
    catch (error) {
        return `0 ${textPlural}`;
    }
    // To calculate the time difference of two dates
    const timeDiff = date2.getTime() - date1.getTime();
    switch (dateDiff) {
        case 'minutes':
            diff = Math.round(timeDiff / (1000 * 60));
            break;
        case 'hours':
            diff = Math.round(timeDiff / (1000 * 3600));
            break;
        default:
            diff = Math.round(timeDiff / (1000 * 3600 * 24));
            break;
    }
    /** Translates datediff e.g. 5 days, 1 day, 2 hours etc. */
    textPlural = trans.get(GENERAL_TRANSLATION, `${getPluralTrans(Math.abs(diff), dateDiff)}`);
    return `${diff} ${textPlural}`;
}

var Priority;
(function (Priority) {
    Priority[Priority["low"] = 1] = "low";
    Priority[Priority["medium"] = 2] = "medium";
    Priority[Priority["high"] = 3] = "high";
})(Priority || (Priority = {}));

/**
 * Returns the color variable based on priority
 * @param value Priority to get color
 * @returns Color variable
 */
function getPriorityColor(value) {
    switch (value) {
        case Priority.high:
            return 'var(--bs-danger)';
        case Priority.medium:
            return 'var(--bs-warning)';
        default:
            return 'var(--bs-success)';
    }
}

/**
 * Check whether abstract control is formgroup
 * @param c Control to check
 * @returns True if it is formGroup
 */
function isFormGroup(c) {
    if (c.controls) {
        return true;
    }
    return false;
}

/**
 * Converts map type into object that can be serialized
 * @param key Serialization key
 * @param value data
 * @returns object for serialization
 */
function mapReplacer(key, value) {
    if (value instanceof Map) {
        return {
            dataType: 'Map',
            value: Array.from(value.entries()), // or with spread: value: [...value]
        };
    }
    else {
        return value;
    }
}

/**
 * Converts object to map type
 * @param key Key
 * @param value Data value
 * @returns Map object
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapReviver(key, value) {
    if (typeof value === 'object' && value !== null) {
        if (value.dataType === 'Map') {
            return new Map(value.value);
        }
    }
    return value;
}

/**
 * Marks all controls in formGroup as touched to invoke validation
 * @param formGroup FormGroup to mark
 */
function markForValidation(formGroup) {
    if (!formGroup) {
        return;
    }
    for (const controlName of Object.keys(formGroup.controls)) {
        const control = formGroup.controls[controlName];
        control.markAsTouched({ onlySelf: true });
        if (isFormGroup(control)) {
            markForValidation(control);
        }
    }
}

/**
 * Removes validation error from control.
 * @param control Control to remove error from.
 * @param errorName Error name to remove.
 */
function RemoveValidationError(control, errorName) {
    if (!control || !control.errors || !control.errors[errorName]) {
        return;
    }
    delete control.errors[errorName];
    if (!Object.keys(control.errors).length) {
        control.setErrors(null);
    }
}

/**
 * Formats string with arguments
 * @param text Text to format
 * @param args Array of arguments
 * @returns Formatted text
 */
function stringFormat(text, args) {
    if (!args.length || !text) {
        return text;
    }
    for (let i = 0; i < args.length; i++) {
        text = text.replace(`{${i}}`, args[i]);
    }
    return text;
}

/**
 * Transforms provided address street, orientation number, city to google maps search format
 * like Galvaniho+9+Bratislava
 * @param address Address to transform
 * @returns Google maps search string
 */
function transformAddressToGoogleMaps(address) {
    if (!address) {
        return undefined;
    }
    let result = undefined;
    result = address.street ? `${result} ${address.street}` : result;
    result = address.orientationNumber
        ? `${result} ${address.orientationNumber}`
        : result;
    result = address.city ? `${result} ${address.city}` : result;
    return result ? result.replace(/ /g, '+') : undefined;
}

/**
 * Translates property based on key
 * @param translation Translation file
 * @param key Key to search
 * @param locale Current locale
 * @returns Translated string or missing value
 */
function translateProperty(translation, key, locale) {
    const missing = '@@Missing:' + key;
    if (!translation || !key || !locale) {
        return missing;
    }
    const trans = translation[locale];
    if (!trans) {
        return missing;
    }
    const transValue = trans[key];
    return transValue ? transValue : missing;
}

/**
 * Update validators for a given form control based on a condition
 * @param control form control
 * @param condition condition based on which validators are used or deleted
 * @param validator form control validator
 * @param disable disable / enable form control
 */
function updateValidators(control, condition, validator, disable) {
    if (condition) {
        control.setValidators(validator);
        if (disable) {
            control.enable();
        }
    }
    else {
        control.clearValidators();
        control.reset();
        if (disable) {
            control.disable();
        }
    }
    control.updateValueAndValidity();
}

class SelectedItem {
    constructor(value, text) {
        this.value = value;
        this.text = text;
    }
}

class SelectedTreeItem {
    constructor(value, text, parentId, hasChild = false) {
        this.value = value;
        this.text = text;
        this.parentId = parentId;
        this.hasChild = hasChild;
        if ((!parentId && parentId !== 0) || parentId === value) {
            this.hasChild = true;
            this.parentId = null;
        }
    }
}

/**
 * Pipe for get absolute number of desired number
 * @param value number which needs to be converted to absolute value
 */
class AbsoluteNumberPipe {
    transform(value) {
        if (!value || isNaN(value)) {
            return undefined;
        }
        return Math.abs(value);
    }
}
AbsoluteNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AbsoluteNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
AbsoluteNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AbsoluteNumberPipe, name: "abs" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AbsoluteNumberPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'abs', pure: true }]
        }] });

/**
 * Compares two dates and returns difference based on dateDiff settings
 * It compares date2 to date1
 *
 * @param {Date} date1 Date which we will calculate
 * @param {Date} date2 Date which will be used as base
 * @param {DateDiff} [dateDiff='days'] Type of response
 * @returns {string} Returns count based on dateDiff
 */
class DateDiffPipe {
    constructor(trans) {
        this.trans = trans;
    }
    transform(value, date2, dateDiff = 'days') {
        return getDateDifference(this.trans, value, date2, dateDiff);
    }
}
DateDiffPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateDiffPipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe });
DateDiffPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateDiffPipe, name: "dateDiff" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateDiffPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dateDiff',
                    pure: true,
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Pipe for date format
 * @param value value which needs to be converted
 * @param format optional format
 * @param customReturnValue optional return value, default @see PIPE_CONSTANTS.DEFAULT_RETURN_VALUE
 * @returns formatted date as string or default value or custom return value
 */
class DateTimePipe {
    constructor(trans) {
        this.trans = trans;
    }
    transform(value, format, customReturnValue) {
        try {
            return formatDate(value, this.trans, format, customReturnValue);
        }
        catch (error) {
            return '';
        }
    }
}
DateTimePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateTimePipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe });
DateTimePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateTimePipe, name: "datetime" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DateTimePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'datetime', pure: true }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Replace null, undefined values to string in dots format '...'
 *
 * @export
 * @class EmptyDotsReplacerPipe
 * @typedef {EmptyDotsReplacerPipe}
 * @implements {PipeTransform}
 */
class EmptyDotsReplacerPipe {
    transform(value) {
        if (!value) {
            return CORE_CONSTANTS.DEFAULT_RETURN_VALUE;
        }
        return value;
    }
}
EmptyDotsReplacerPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EmptyDotsReplacerPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EmptyDotsReplacerPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EmptyDotsReplacerPipe, name: "emptyDotsReplacer" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EmptyDotsReplacerPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'emptyDotsReplacer',
                    pure: true,
                }]
        }] });

class EnumToSelectPipe {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transform(value) {
        return (Object.values(value)
            .filter((o) => typeof o === 'number')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((o) => {
            return { text: value[o], value: o };
        }));
    }
}
EnumToSelectPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToSelectPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EnumToSelectPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToSelectPipe, name: "enumToSelect" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToSelectPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'enumToSelect',
                }]
        }] });

class EnumToValueStringPipe {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    transform(value, source) {
        const data = Object.keys(source).find((p) => p === value);
        if (data) {
            return source[value];
        }
        else {
            return '';
        }
    }
}
EnumToValueStringPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToValueStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
EnumToValueStringPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToValueStringPipe, name: "enumToValueString" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: EnumToValueStringPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'enumToValueString',
                }]
        }] });

/**
 * Pipe for format amount
 * @param amount amount which needs to be converted
 */
class FormatAmountPipe {
    constructor(translation) {
        this.translation = translation;
    }
    transform(amount) {
        return formatAmount(amount, this.translation.getFullLocale);
    }
}
FormatAmountPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatAmountPipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe });
FormatAmountPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatAmountPipe, name: "formatAmount" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatAmountPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'formatAmount',
                    pure: true,
                }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

class FormatBytesPipe {
    transform(size, decimalPlaces) {
        return formatBytes(size, decimalPlaces);
    }
}
FormatBytesPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatBytesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
FormatBytesPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatBytesPipe, name: "formatBytes" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: FormatBytesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'formatBytes',
                    pure: true,
                }]
        }] });

class PriorityColorPipe {
    transform(value) {
        return getPriorityColor(value);
    }
}
PriorityColorPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PriorityColorPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
PriorityColorPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PriorityColorPipe, name: "priorityColor" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PriorityColorPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'priorityColor',
                }]
        }] });

/*
 * Pipe for bypassing default Angular sanitization
 * Taken from https://github.com/SwarnaKishore/angular-safe-pipe
 */
class SafePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value, type) {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Invalid safe type specified: ${type}`);
        }
    }
}
SafePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SafePipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
SafePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SafePipe, name: "safe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: SafePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'safe',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.DomSanitizer }]; } });

class StringFormatPipe {
    transform(text, args) {
        return stringFormat(text, args);
    }
}
StringFormatPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StringFormatPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
StringFormatPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StringFormatPipe, name: "stringFormat" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: StringFormatPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'stringFormat',
                    pure: true,
                }]
        }] });

class UnknownToObjectPipe {
    // eslint-disable-next-line @typescript-eslint/ban-types
    transform(data) {
        return Object.assign({}, data);
    }
}
UnknownToObjectPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UnknownToObjectPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
UnknownToObjectPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UnknownToObjectPipe, name: "unknownToObject" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: UnknownToObjectPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'unknownToObject',
                }]
        }] });

/**
 * Pipe used to correctly format currency values
 *
 * Internally it uses Translation service to get correct locale based on currency string
 *
 * @param value Value to format of type ValueCurrency @see {@link ValueCurrency}
 * @param precision Decimal place precision in string format `{minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}`. See `DecimalPipe` for more details.
 * @default 1.2
 */
class ValueCurrencyPipe {
    constructor(trans) {
        this.trans = trans;
    }
    transform(value, precision = '1.2') {
        return valueCurrencyTransform(value, this.trans, precision);
    }
}
ValueCurrencyPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueCurrencyPipe, deps: [{ token: TranslationService }], target: i0.ɵɵFactoryTarget.Pipe });
ValueCurrencyPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueCurrencyPipe, name: "valueCurrency" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueCurrencyPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'valueCurrency', pure: true }]
        }], ctorParameters: function () { return [{ type: TranslationService }]; } });

/**
 * Pipe used to format value suffix to format e.g. 1000 €, 1h, etc.
 *
 * @param value Value to format of type ValueSuffix @see {@link ValueSuffix}
 * @param addSpace If true adds space between value and suffix
 * @default false
 */
class ValueSuffixPipe {
    transform(value, addSpace = false) {
        if (!value || !value.value) {
            return '';
        }
        return `${value.value}${addSpace ? ' ' : ''}${value.suffix}`;
    }
}
ValueSuffixPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueSuffixPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ValueSuffixPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueSuffixPipe, name: "valueSuffix" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ValueSuffixPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'valueSuffix', pure: true }]
        }] });

class PipesModule {
}
PipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PipesModule, declarations: [EnumToSelectPipe,
        UnknownToObjectPipe,
        EnumToValueStringPipe,
        DateTimePipe,
        FormatBytesPipe,
        SafePipe,
        ValueCurrencyPipe,
        FormatAmountPipe,
        AbsoluteNumberPipe,
        DateDiffPipe,
        StringFormatPipe,
        EmptyDotsReplacerPipe,
        ValueSuffixPipe,
        PriorityColorPipe], exports: [EnumToSelectPipe,
        UnknownToObjectPipe,
        EnumToValueStringPipe,
        DateTimePipe,
        FormatBytesPipe,
        SafePipe,
        ValueCurrencyPipe,
        FormatAmountPipe,
        AbsoluteNumberPipe,
        DateDiffPipe,
        StringFormatPipe,
        EmptyDotsReplacerPipe,
        ValueSuffixPipe,
        PriorityColorPipe] });
PipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PipesModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        EnumToSelectPipe,
                        UnknownToObjectPipe,
                        EnumToValueStringPipe,
                        DateTimePipe,
                        FormatBytesPipe,
                        SafePipe,
                        ValueCurrencyPipe,
                        FormatAmountPipe,
                        AbsoluteNumberPipe,
                        DateDiffPipe,
                        StringFormatPipe,
                        EmptyDotsReplacerPipe,
                        ValueSuffixPipe,
                        PriorityColorPipe,
                    ],
                    exports: [
                        EnumToSelectPipe,
                        UnknownToObjectPipe,
                        EnumToValueStringPipe,
                        DateTimePipe,
                        FormatBytesPipe,
                        SafePipe,
                        ValueCurrencyPipe,
                        FormatAmountPipe,
                        AbsoluteNumberPipe,
                        DateDiffPipe,
                        StringFormatPipe,
                        EmptyDotsReplacerPipe,
                        ValueSuffixPipe,
                        PriorityColorPipe,
                    ],
                }]
        }] });

const MAIN_APP = new InjectionToken('MainAppToken');

class XsrfInterceptor {
    constructor(tokenExtractor) {
        this.tokenExtractor = tokenExtractor;
    }
    intercept(req, next) {
        const token = this.tokenExtractor.getToken();
        if (token !== null && !req.headers.has('X-XSRF-TOKEN')) {
            req = req.clone({ headers: req.headers.set('X-XSRF-TOKEN', token) });
        }
        return next.handle(req);
    }
}
XsrfInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: XsrfInterceptor, deps: [{ token: i1.HttpXsrfTokenExtractor }], target: i0.ɵɵFactoryTarget.Injectable });
XsrfInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: XsrfInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: XsrfInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpXsrfTokenExtractor }]; } });

const REGEX = {
    ONLY_NUMBERS: '[0-9]+',
    PHONE: '^(\\+)?\\d+',
    IBAN: '^[A-Z]{2}(?:[ ]?[0-9]){18,22}$',
    BIC: '^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$',
};

/**
 * Validates if from date is before to date.
 * Default values for from and to control names are 'from' and 'to'.
 * You can change it as needed.
 * @param fromControlName From control name.
 * @param toControlName To control name.
 */
function dateRangeValidator(fromControlName = 'from', toControlName = 'to') {
    return (control) => {
        const fromControl = control.get(fromControlName);
        const toControl = control.get(toControlName);
        RemoveValidationError(fromControl, 'dateRange');
        RemoveValidationError(toControl, 'dateRange');
        if (fromControl && toControl && fromControl.value && toControl.value) {
            const fromDate = new Date(fromControl.value);
            const toDate = new Date(toControl.value);
            if (fromDate >= toDate) {
                fromControl.setErrors({ dateRange: true });
                toControl.setErrors({ dateRange: true });
                return { dateRange: true };
            }
        }
        return null;
    };
}

/**
 * Validates if the value length of the given form control exceeds the maximum number of values
 * @param fromControlName From control name.
 * @param maxValues Max value number.
 */
function maxValuesValidator(formControlName = '', maxValues = 0) {
    return (control) => {
        const formControl = control.get(formControlName);
        RemoveValidationError(formControl, 'maxValues');
        if (formControl &&
            formControl.value &&
            formControl.value.length > maxValues) {
            formControl.setErrors({
                maxValues: true,
            });
            return { maxValues: true };
        }
        return null;
    };
}

/**
 * Validates if the value length of the given form control exceeds the minimum number of values
 * @param fromControlName From control name.
 * @param minValues Min value number.
 */
function minValuesValidator(formControlName = '', minValues = 0) {
    return (control) => {
        const formControl = control.get(formControlName);
        RemoveValidationError(formControl, 'minValues');
        if (formControl &&
            formControl.value &&
            formControl.value.length < minValues) {
            formControl.setErrors({
                minValues: true,
            });
            return { minValues: true };
        }
        return null;
    };
}

/*
 * Public API Surface of asol-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ANONYM_URL, API_BASE_URI, AUTH_REDIRECT, AbsoluteNumberPipe, ApiDataService, AsolCoreModule, AsolMediaQueryService, AuthGuard, AuthInterceptorService, AuthenticationServiceBase, CONTENT_TYPE, CORE_CONSTANTS, CZ, DEFAULT_LANGUAGE, DateDiffPipe, DateTimePipe, EN, ERROR_TRANSLATION, EmptyDotsReplacerPipe, EnumToSelectPipe, EnumToValueStringPipe, FileDataService, FormatAmountPipe, FormatBytesPipe, GENERAL_TRANSLATION, HOME_REDIRECT, HomeGuard, LANGUAGES, LOGIN_REDIRECT, LanguageInterceptor, MAIN_APP, MAIN_APP_CONST, MEDIA_QUERY, PipesModule, Priority, PriorityColorPipe, REGEX, RemoveValidationError, SK, SafePipe, SelectedItem, SelectedTreeItem, StringFormatPipe, TenantInterceptorService, TranslationService, UnknownToObjectPipe, ValueCurrencyPipe, ValueSuffixPipe, XsrfInterceptor, collectionMapper, dateRangeValidator, formatAmount, formatBytes, formatDate, generateId, getDateDifference, getPluralTrans, getPriorityColor, isFormGroup, isValueCurrency, mapReplacer, mapReviver, markForValidation, maxValuesValidator, minValuesValidator, stringFormat, transformAddressToGoogleMaps, translateProperty, updateValidators, valueCurrencyTransform };
